import React from "react";
import { Link } from "gatsby";

export const malacologyTextEn = (
  <div>
    <Link to="/malacology-group">Group members and selected publications</Link>
    <p>
      With over 42,000 species, non-marine molluscs make up almost one-half of
      all molluscan biodiversity. In spite of this the great majority of
      research remains focused on marine taxa. Our research group is committed
      to bringing modern approaches and techniques to the empirical analysis of
      non-marine molluscs. We focus on the patterns and processes ranging from
      taxonomy, faunistics and evolution to community, paleo- and macro-
      ecology. From this we are able to better document biodiversity and
      identify optimum conservation strategies for the second most diverse group
      of terrestrial organisms.
    </p>
    <ul>
      <li>
        <b>Taxonomy and evolution.</b> Effective biodiversity protection must be
        based on empirically validated taxonomic concepts. We address this by
        conducting integrative taxonomic revisions which identify biologically
        valid taxa by searching for consensus among mtDNA, nDNA, morphometric,
        anatomical, ecological and biogeography signals. We use the resulting
        phylogenetic trees to document evolutionary process, and are especially
        interested in identifying the mechanisms underlying evolutionary change
        and diversification rates.
      </li>
      <li>
        <b>Faunistics.</b> Knowing the accurate makeup of molluscan assemblages
        is an essential foundation for all subsequent ecology and conservation
        projects. Our group has sampled over 5000 sites across the Holarctic
        representing 1000s of species and millions of shells, with an emphasis
        on central and northern Europe, central Asia, and North America. We have
        also investigated error rates present in mined data sources such as
        museum records. Our work has lead to numerous reports to conservation
        and other agencies. We have also produced field guides to allow
        identification of the fauna.
      </li>
      <li>
        <b>Phylogeography.</b> Because mollusc DNA is best preserved in dry
        material, our samples collected across the northern hemisphere provide a
        viable substrate for the genetic analyses. When combined with
        comprehensive datasets on local habitat conditions, fossil record,
        historical factors, and climate (since the LGM 21,000 yr BP to the
        present), we are able to draw complex phylogeographic conclusions and
        explore patterns of colonization dynamics, gene-flow, and role of
        glacial refugia.
      </li>
      <li>
        <b>Community ecology.</b> Every single individual is set in a complex
        web of biotic and abiotic interactions within its populations,
        communities, and ecosystems. In our group we seek to unravel the causes
        of diversity, composition, and vulnerability of molluscan communities,
        taking into account ongoing climate and environmental changes. We
        collect and analyze data on ecological variables, historical factors
        (using extensive palaeoecological record), and climate, and compare
        molluscs with other taxa (e.g. vascular plants and bryophytes). By
        integrating these sources of information we are able to deeply
        understand the causality behind the composition and functioning of
        molluscan communities.
      </li>
      <li>
        <b>Genomics.</b> Currently, species-scale nDNA signal is largely limited
        to ITS1 and ITS2 in the rRNA Gene Cassette. However, we have recently
        initiated the project to identify low-copy nDNA regions which provide
        species-scale signal and can be amplified using universal primers. Our
        first newly discovered region, Intron 8 in the ELAV gene, not only
        provides excellent phylogenetic signal within a genus but also recovers
        deep phylogenetic pattern. We look forward to identifying more areas via
        additional investigations of genomic data covering the Panpulmonata. We
        are also involved in comparative genomics and mapping of the mtGenome
        across the Panpulmonata.
      </li>
    </ul>
    <p>
      Our research also covers a variety of other topics, including
      physiological ecology (using stable isotope analyses), macroecology
      (barriers and regional species pools), and palaeoecology. Under the
      guidance of our group members, a variety of bachelor, master and doctoral
      theses covering all the above-mentioned thematic areas are being
      conducted. We are keen to cooperate with scientists and mollusc
      enthusiasts from other countries and institutions, to share and exchange
      ideas, knowledge, experiences, and study material.
    </p>
    <p>
      <b>Our colleagues and friends from abroad include:</b> Chris Wade, Gary
      Rosenberg, Rodrigo Salvador, Martin Haase, Gary Barker, Richard Preece,
      Satoshi Chiba, Robert Cameron, Julien Ryelandt, Eike Neubert, Brian Coles,
      Barna Páll-Gergely, Duda Michael, Julien Ryelandt, Quentin Wackenheim,
      Oihana Razkin Aguirre, Adrienne Jochum, Levan Mumladze, Enrico Schwabe,
      Ted von Proschwitz, Charles Drost, Jacob Vanhouten, Nicole
      Limondin-Lozouet, Salomé Granai, Olivier Moine, Coenraad Adema, Grita
      Skujiene, Bert Van Bocxlaer, Bernhard Hausdorf, Anna Sulikowska-Drozd,
      Edmund Gittenberger, Kazuki Kimura, Frank Koehler, Gary Barker, John
      Slapcinsky, Timothy A. Pearce, Paul Callomon, Vollrath Wiese, Sigrid Hof,
      Ira Richling, Emmanuel Fara, Junn Kitt Foon, Karin Urfer, John Stanisic,
      Francisco Welter-Schultes, Dan Dourson, Owen Griffiths, Peter
      Tattersfield, Ben Rowson, Thomas Neubauer, Anatoly Schileyko, Ivailo
      Dedov, Igor Balashov, Amy Shrader Van Devender, Robert Wayne Van Devender,
      Marco Bodon, Falco Giusti, Kostas Triantis, Jeffrey Pigati, Bin Ye,
      Christiane Richter‬, Frank Walter, Heike Reise, John M.C. Hutchinson, David
      Holyoak. And many others...
    </p>
    <p>
      We also collaborate and regularly meet with numerous{" "}
      <b>Czech and Slovak malacologists</b>:{" "}
      <a href="http://mollusca.sav.sk/malacology/people.htm">
        http://mollusca.sav.sk/malacology/people.htm
      </a>
    </p>
    <p>
      <u>
        <b>Join us:</b>
      </u>
      <br />
      For questions, ideas, requests, etc. feel free to contact any member of{" "}
      <Link to="/malacology-group">our group</Link>. The main person to contact
      regarding the web page content is Veronika Horsáková (
      <a href="mailto:veronika.horsakova@seznam.cz">
        veronika.horsakova@seznam.cz
      </a>
      ).
    </p>
  </div>
);
export const malacologyTextCz = (
  <div>
    <Link to="/malacology-group">Členové skupiny a vybrané publikace</Link>
    <p>
      Terestričtí a sladkovodní měkkýši představují s více než 42 000 druhy
      téměř polovinu veškeré biologické rozmanitosti měkkýšů. Navzdory tomu se
      převážná většina výzkumu stále zaměřuje na mořské taxony. V naší skupině
      usilujeme o zavedení moderních analytických přístupů a technologií do
      empirické analýzy měkkýšů, vyjma mořských. Zabýváme se zákonitostmi a
      procesy napříč pestrou škálou odvětví malakologie, od taxonomie,
      faunistiky a evoluce až po ekologii společenstev, paleo- a makroekologii.
      Tyto poznatky nám umožňují lépe pochopit biologickou rozmanitost a
      navrhovat účinné strategie ochrany této druhé nejpočetnější skupiny
      suchozemských organismů.
    </p>
    <ul>
      <li>
        <b>Taxonomie a evoluce.</b> Základním pilířem ochrany biodiverzity jsou
        empiricky ověřené taxonomické jednotky (nejčastěji druhy). Takové
        jednotky se snažíme dokumentovat prostřednictvím integrativních
        taxonomických revizí, které nám umožňují identifikovat biologicky
        validní taxony na základě shody genetických (mitochondriální a jaderná
        DNA), morfometrických, anatomických, ekologických a biogeografických
        znaků. Fylogenetické analýzy využíváme také k vyhodnocení evolučních
        procesů a mechanismů, které souvisí s evolučními změnami a rychlostmi
        diverzifikace.
      </li>
      <li>
        <b>Faunistika.</b> Detailní znalost složení společenstev měkkýšů
        představuje nezbytný základ pro veškeré navazující ekologické či
        ochranářské výzkumy. Členové naší skupiny odebírali vzorky měkkýšů na
        více než 5000 lokalitách v oblasti Holoarktu, čítající tisíce druhů a
        miliony schránek, především z území střední a severní Evropy, střední
        Asie a Severní Ameriky. Zkoumali jsme také chybovost v externích
        zdrojích dat, např. v muzejních záznamech. Podílíme se na přípravě zpráv
        pro orgány ochrany přírody a dalších publikací, jako jsou brožury a
        průvodce k určování měkkýší fauny.
      </li>
      <li>
        <b>Fylogeografie.</b> Měkkýší DNA se nejlépe zachovává v suchém
        materiálu, což umožňuje využití našich vzorků, sbíraných napříč regiony
        severní polokoule, pro genetické analýzy. Výsledky těchto analýz
        propojujeme s rozsáhlými daty o stanovištních podmínkách, historických
        faktorech a klimatu (od posledního glaciálního maxima zhruba před 21 000
        lety po současnost), což nám umožňuje vyvozovat obecné fylogeografické
        závěry a zkoumat kolonizační dynamiku, tok genů nebo roli glaciálních
        refugií.
      </li>
      <li>
        <b>Ekologie společenstev.</b> Každý jedinec je součástí sítě biotických
        a abiotických interakcí uvnitř populací, společenstev a ekosystémů. V
        naší skupině se snažíme odhalit příčiny rozmanitosti, složení a
        zranitelnosti měkkýších společenstev s ohledem na probíhající klimatické
        a environmentální změny. Sbíráme a analyzujeme údaje o ekologických
        podmínkách, historických faktorech (s využitím rozsáhlých
        paleoekologických záznamů), klimatu a skladbě jiných taxonů v rámci
        téhož ekosystému (např. cévnatých rostlin a mechorostů). Díky propojení
        těchto zdrojů informací jsme schopni porozumět příčinným souvislostem,
        které definují složení a fungování měkkýších společenstev.
      </li>
      <li>
        <b>Genomika.</b> V současné době je signál nDNA umožňující rozlišení
        druhů omezen především na rRNA fragmenty ITS1 a ITS2. Náš nedávno
        zahájený projekt je zaměřen na identifikaci fragmentů nDNA o nízkém
        počtu kopií, které poskytují druhový signál a které lze amplifikovat
        pomocí univerzálních primerů. Již první nově objevený úsek DNA, Intron 8
        v genu ELAV, má nejen vynikající fylogenetický vnitrorodový signál, ale
        umožňuje také rekonstrukci hlubší fylogeneze. Těšíme se na identifikaci
        dalších úseků DNA v rámci probíhajících výzkumů genomických dat
        pokrývajících taxon Panpulmonata. Rovněž se zabýváme srovnávací
        genomikou a mapováním mitochondriálního genomu plžů.
      </li>
    </ul>
    <p>
      Náš výzkum zahrnuje také řadu dalších témat, včetně fyziologie (s využitím
      analýzy stabilních izotopů), makroekologie (bariéry a regionální druhová
      bohatost) a paleoekologie. Pod vedením členů naší skupiny vzniká pestrá
      škála bakalářských, magisterských i doktorských závěrečných prací
      pokrývajících veškeré výše uvedené tematické okruhy. Jsme vždy nakloněni
      spolupráci s vědci a milovníky měkkýšů z jiných zemí a institucí, se
      kterými sdílíme nápady, znalosti, zkušenosti i výzkumný materiál.
    </p>
    <p>
      <b>Mezi naše externí kolegy a přátele patří:</b> Chris Wade, Gary
      Rosenberg, Rodrigo Salvador, Martin Haase, Gary Barker, Richard Preece,
      Satoshi Chiba, Robert Cameron, Julien Ryelandt, Eike Neubert, Brian Coles,
      Barna Páll- Gergely, Duda Michael, Julien Ryelandt, Quentin Wackenheim,
      Oihana Razkin Aguirre, Adrienne Jochum, Levan Mumladze, Enrico Schwabe,
      Ted von Proschwitz, Charles Drost, Jacob Vanhouten, Nicole Limondin-
      Lozouet, Salomé Granai, Olivier Moine, Coenraad Adema, Grita Skujiene,
      Bert Van Bocxlaer, Bernhard Hausdorf, Anna Sulikowska-Drozd, Edmund
      Gittenberger, Kazuki Kimura, Frank Koehler, Gary Barker, John Slapcinsky,
      Timothy A. Pearce, Paul Callomon, Vollrath Wiese, Sigrid Hof, Ira
      Richling, Emmanuel Fara, Junn Kitt Foon, Karin Urfer, John Stanisic,
      Francisco Welter-Schultes, Dan Dourson, Owen Griffiths, Peter
      Tattersfield, Ben Rowson, Thomas Neubauer, Anatoly Schileyko, Ivailo
      Dedov, Igor Balashov, Amy Shrader Van Devender, Robert Wayne Van Devender,
      Marco Bodon, Falco Giusti, Kostas Triantis, Jeffrey Pigati, Bin Ye,
      Christiane Richter‬, Frank Walter, Heike Reise, John M.C. Hutchinson, David
      Holyoak. A mnoho dalších...
    </p>
    <p>
      Spolupracujeme a pravidelně se setkáváme s kolegy a přáteli z řad{" "}
      <b>českých a slovenských malakologů</b>:{" "}
      <a href="http://mollusca.sav.sk/malacology/people.htm">
        http://mollusca.sav.sk/malacology/people.htm
      </a>
    </p>
    <p>
      <u>
        <b>Přidejte se k nám:</b>
      </u>
      <br />S dotazy, návrhy či připomínkami se neváhejte obracet na členy{" "}
      <Link to="/malacology-group">naší skupiny</Link>. Za obsah stránky skupiny
      odpovídá Veronika Horsáková (
      <a href="mailto:veronika.horsakova@seznam.cz">
        veronika.horsakova@seznam.cz
      </a>
      ).
    </p>
  </div>
);
export const cryptogamologyTextCz = (
  <span>
    Zabýváme se detailním výzkumem řas, mechorostů a lišejníků. Tyto skupiny se
    dříve řadily do skupiny tajnosnubných (výtrusných) rostlin, tak zvaných
    kryptogamů. Náš zájem o kryptogamy dává smysl, protože{" "}
    <Link to="/topic-mires">rašeliniště</Link> i některé další{" "}
    <Link to="/topic-relict">reliktní ekosystémy</Link> se vyznačují velkou
    pestrostí kryptogamů a jejich klíčovými rolemi ve fungování těchto
    ekosystémů. Kryptogamy mají rovněž značný význam jako biondikátory v{" "}
    <Link to="/topic-paleo">paleoekologii</Link>. Náš zájem o řasy, mechorosty a
    lišejníky však tyto zmíněné obory přesahuje. Zabýváme se rovněž taxonomií,
    genetickou strukturou a ekologií různých skupin krytogamů, kromě
    rašeliništních druhů například i epifytickými mechorosty, terestrickými
    lišejníky v borových lesích a v arkto-alpinské tundře a vodními a půdními
    rozsivkami. Algologický výzkum je zaměřen především na rozsivky v polárních
    oblastech, kde patří tyto jednobuněčné řasy s křemičitou schránkou k
    nejdůležitějším primárním producentům. Snažíme se rozluštit celou řadu
    rozsivkových taxonomických záhad a přispět tím k lepšímu pochopení
    biogeografie mikroorganismů. Rozsivkové schránky zachované v sedimentech
    studujeme v rámci multiproxy{" "}
    <Link to="/topic-paleo">paleoekologických projektů</Link>. Neomezujeme se
    však jen na rozsivky, objekty našeho zájmu jsou i sinice a řasy v řekách,
    rybnících nebo jeskyních Moravského Krasu. Bryologický a lichenologický
    výzkum se zabývá druhovou diverzitou v chráněných územích s důrazem na
    monitoring vzácných druhů. Specializovanější projekty zahrnují například
    studium bioindikační role epifytických druhů, sledování zachovalosti
    pralesovitých porostů na druzích tlejícího dřeva a monitorování úspěšnosti
    revitalizačních zásahů na rašeliništích. Pro naši práci využíváme i
    nejmodernější molekulární metody (např. next-generation sequencing), kde se
    soustředíme hlavně na odhalení vnitrodruhové struktury populací, odrazem
    prostředí v genetické struktuře druhů a na řešení nejasných taxonomických
    otázek.
  </span>
);

export const cryptogamologyTextEn = (
  <span>
    We focus in detail on the research of algae, bryophytes and lichens. These
    groups were all formerly classified as “cryptogams” (spore plants). Our
    interest in cryptogams makes sense, because{" "}
    <Link to="/topic-mires">mires</Link> as well as other{" "}
    <Link to="/topic-relict">relict ecosystems</Link>, have uniquely rich
    communities of cryptogams, which play key roles for the functioning of these
    ecosystems. Cryptogams are also of significant importance as bioindicators
    in <Link to="/topic-paleo">palaeoecology</Link>. However, our interest in
    algae, bryophytes and lichens exceeds the above mentioned research fields.
    We also deal with the taxonomy, genetic structure and ecology of various
    cryptogam groups, not only mire species, but also epiphytic bryophytes,
    terrestrial lichens in pine forests or arctic-alpine tundra, and freshwater
    and soil diatoms. Algological research focuses mainly on the diatoms in
    polar regions, in which these unicellular organisms with siliceous shells
    represent the most important primary producers. We try to disentangle a
    variety of diatom taxonomical mysteries and contribute to a better
    understanding of the biogeography of microorganisms. Diatom shells,
    preserved in sediments, are studied in the course of multiproxy{" "}
    <Link to="/topic-paleo">palaeoecological projects</Link>. However, we do not
    restrict our focus to diatoms, as we also deal with the blue-green algae and
    algae in rivers, ponds and caves of the Moravian Karst. Bryological and
    lichenological research focuses on the species diversity in protected areas,
    with the emphasis on the monitoring of rare species. In the specialized
    projects we study, e.g. the bioindication potential of epiphytic species,
    state of old-growth forests based on the composition of species on decaying
    wood, and monitoring of restored mires. For our work we use as well the most
    up-to-date molecular methods (e.g. next-generation sequencing), to reveal
    intra-specific population structure, impact of environment on the genetic
    structure of species, and resolving unclear taxonomic issues.
  </span>
);

export const mireEcologyTextEn = (
  <span>
    In our research we try to disentangle the causes of the diversity and
    vulnerability of various mire types. Mires are wetland ecosystems with
    permanently high water levels and limited nutrient availability. A great
    variety of rare and endangered species is associated with these ecosystems,
    which are, however, quickly disappearing from the central-European
    landscape. Groundwater-supplied mires, so called fens, represent the most
    threatened mire type. In Europe only small remnants have been preserved,
    mainly as regularly mown fen meadows. Rainwater-supplied mires, so called
    bogs, are unique mainly for playing a key role in biogeochemical cycles. In
    our research we focus on both these mire types, with a greater emphasis on
    more threatened fens. We study the influence of water chemistry on the
    species diversity of various groups of fen organisms, especially plants and
    molluscs, but also <Link to="/topic-crypto">algae</Link>, protozoans and
    fungi. We are interested in both mineral content of groundwater, which
    drives the variation among well- preserved fens, and nutrient availability,
    which varies due to the human interference with the biogeochemical cycles.
    Apart from water chemistry we also explore other important ecological
    variables. These are mainly temperature and hydrology, i.e. factors that
    severely change upon the influence of current climate changes, making mire
    ecosystems so easily vulnerable. Using the methods of{" "}
    <Link to="/topic-paleo">palaeoecology</Link> we also include{" "}
    <Link to="/topic-relict">historical factors</Link> to our analyses, which
    often appear as key for the species diversity of mires. Comparing multiple
    taxonomic groups we are able to reach more general conclusions and higher
    resolution in our understanding of relationships between biotic and abiotic
    factors. We also deal with the functional links between the individual
    groups of organisms, such as <Link to="/topic-crypto">bryophytes</Link> and
    vascular plants. To obtain a broader picture of the vulnerability of mires,
    we explore these ecosystems across the whole of Europe. We synthesize
    literature data, but we also collect our own accurate data about various
    organisms and environmental parameters, using the same methodology in all
    the regions. However, we also pay attention to the current state of mires in
    particular regions in relation to ongoing changes of mire ecosystems.
    Ultimately, we seek for the type of management or other direct activities
    that might help saving the diversity of fen ecosystems in our landscape.
  </span>
);

export const mireEcologyTextCz = (
  <span>
    Hlavním tématem našeho výzkumu je snaha o pochopení příčin pestrosti a
    zranitelnosti různých typů rašelinišť. Rašeliniště jsou ekosystémy s trvale
    vysokou hladinou vody a nedostatkem přístupných živin. Vyskytuje se na nich
    celá řada vzácných a ohrožených organismů. Ve střední Evropě však
    rašeliniště velmi rychle mizí. Nejohroženější jsou rašeliniště sycená
    podzemní vodou, takzvaná slatiniště. Ve střední Evropě se zachovaly jen
    jejich zbytky, a to převážně jako sečené slatinné louky. Rašeliniště sycená
    srážkovou vodou, takzvaná vrchoviště, jsou zase významná z důvodu jejich
    klíčové role v biogeochemických cyklech. V našem výzkumu se zaměřujeme na
    oba tyto hlavní typy rašelinišť, s větším důrazem na ohroženější slatiniště.
    Zkoumáme vliv chemismu prostředí na druhovou pestrost různých rašeliništních
    organismů, zejména rostlin a měkkýšů, ale i{" "}
    <Link to="/topic-crypto">řas</Link>, prvoků nebo hub. Zajímá nás jak vliv
    minerálů ve vodě, který ovlivňuje druhové rozdíly mezi dobře zachovalými
    rašeliništi, tak i vliv přístupnosti živin, který se mění kvůli lidským
    zásahům do biogeochemických cyklů. Kromě chemismu prostředí sledujeme i
    další důležité ekologické faktory. K nim patří teplota a hydrologický režim,
    tedy faktory, které se během současných klimatických změn prudce mění a tím
    se rašeliniště stávají zranitelnými. Pomocí{" "}
    <Link to="/topic-paleo">paleoekologických metod</Link> zahrnujeme do našich
    analýz i <Link to="/topic-relict">historické faktory</Link>, které se mnohdy
    ukazují pro diverzitu rašelinišť jako klíčové. Srovnáváním různých
    taxonomických skupin jsme schopni dosáhnout větší míry zobecnění a přesnosti
    v našem chápání vztahů mezi neživou a živou přírodou. Zkoumáme i funkční
    vazby mezi jednotlivými skupinami organismů, například mezi{" "}
    <Link to="/topic-crypto">mechorosty</Link> a cévnatými rostlinami. Abychom
    dobře pochopili naše ohrožené biotopy rašelinišť, zkoumáme rašeliniště v
    celé Evropě. Syntetizujeme data z literatury, ale získáváme i nová, přesná
    data o různých skupinách organismů a parametrech prostředí pomocí stejné
    metodologie v různých územích. Stejně tak se ale zabýváme i výzkumem
    aktuálního stavu rašelinišť v jednotlivých oblastech a současnými změnami
    rašeliništních ekosystémů. Zjišťujeme, jaký typ obhospodařování nebo jiných
    aktivních zásahů do ekosystému může pomoci zachránit pestrost slatiništních
    ekosystémů v naší krajině.
  </span>
);

export const palaeoEcologyTextEn = (
  <span>
    In our research we also try to learn about historical changes of our
    landscape, such as openness, forest cover and species composition of various
    ecosystems, in relation to climate changes and human impact. Such knowledge
    helps us to understand the current patterns of biota. This type of research
    is called „palaeoecology“, i.e. the study of interactions between organisms
    and their past environments. The sources of information and objects of our
    interest are represented by various sediment types, preserving remains of
    organisms. We focus mainly on the{" "}
    <Link to="/topic-mires">organic sediments of lakes, bogs and fens</Link>, in
    which we can read as if we had a rare chronicle, but which capture a time
    period far longer than any human- made documents do. The development of
    these “natural archives” is facilitated by a slow accumulation of sediments
    due to permanent water saturation, which prevents the decomposition of
    organic matter. In our research we use both biological objects (so called
    “subfossils”), and geochemical and sedimentological analyses. The age can be
    accurately estimated via the radiocarbon dating. The data about the species
    occurrence allow us to reconstruct past environmental changes qualitatively,
    as well as quantitatively using modern statistical tools (i.e. transfer
    functions). The core of our research territory lies in the Western
    Carpathians and adjacent regions, such as Eastern Carpathians, eastern
    Sudetes and Morava valleys. We identify a broad{" "}
    <Link to="/topic-crypto">spectrum of organisms</Link>: mainly pollen, seeds,
    fruits, plant tissues, bryophytes, wood, shells of diatoms (link) and
    testate amoebae, chitinous remains of invertebrates (e.g. chironomid larvae)
    and mollusc shells. The combination of multiple taxa makes the
    reconstructions more accurate and allows us to estimate changes of
    temperature (chironomids), moisture (testate amoebae), calcium richness
    (plant macroremains, molluscs) and nutrient richness (diatoms). The
    knowledge that we obtain is also interconnected with current ecology – we
    study the influence of past landscape development on the contemporary
    variation of flora and fauna and distribution of species, including
    threatened <Link to="/topic-relict">relicts</Link>.
  </span>
);

export const palaeoEcologyTextCz = (
  <span>
    V našem výzkumu se také snažíme zjistit, jak se v minulosti měnila krajina,
    její lesnatost a druhové složení jednotlivých biotopů v závislosti na
    změnách klimatu a vlivu člověka. Tyto znalosti nám pomáhají pochopit podobu
    současné živé přírody. Takový výzkum označujeme jako paleoekologii, což je
    věda o vztazích organismů a jejich prostředí v minulosti. Zdrojem informací
    a objektem zkoumání jsou nejrůznější sedimenty obsahující zachovalé zbytky
    organizmů. V případě našeho výzkumu se většinou jedná o{" "}
    <Link to="/topic-mires">
      organické sedimenty jezer, vrchovišť a slatinišť
    </Link>
    , ve kterých čteme jako ve vzácné kronice, ale zachycující výrazně delší
    časový úsek než všechny lidské písemnosti. Postupného ukládání sedimentu a
    vzniku těchto „přírodních archivů“ je dosaženo díky vysoké hladině vody,
    která zabraňuje rozkladu zbytků většiny organizmů. Využíváme jak analýzu
    biologických objektů (tzv. subfosilií), tak i geochemické a sedimentologické
    analýzy. Stáří se přesně stanovuje za pomoci radiokarbonového datování.
    Údaje o výskytu druhů pak vedou k rekonstrukci změn prostředí v minulosti, a
    to jak kvalitativně, tak i kvantitativně pomocí moderních statistických
    nástrojů (tzv. přenosové funkce). Oblastí našeho výzkumu jsou celé Západní
    Karpaty a území s nimi sousedící, jako jsou například Východní Karpaty,
    východní Sudety nebo moravské úvaly. Určujeme široké spektrum{" "}
    <Link to="/topic-crypto">zbytků organismů</Link>: nejčastěji pylová zrna,
    semena, plody a pletiva rostlin, mechorosty, dřevo, schránky rozsivek a
    krytenek, chitinové zbytky bezobratlých (např. larev pakomárů) a ulity
    měkkýšů. Kombinace více skupin rekonstrukci zpřesňuje a umožňuje odhadovat
    změny teploty (pakomáři), vlhkosti (krytenky), vápnitosti (makrozbytky,
    měkkýši) a úživnosti (rozsivky). Zjištěné výsledky rovněž propojujeme se
    současnou ekologií – ověřujeme vliv dávné historie naší krajiny na současnou
    pestrost flóry a fauny a současné rozšíření jednotlivých druhů, včetně
    ohrožených <Link to="/topic-relict">reliktů</Link>.
  </span>
);

export const relictEcosystemsTextEn = (
  <span>
    One of the most fascinating and unique characters of{" "}
    <Link to="/topic-mires">mires and fens</Link> is their ability to act as
    refugia for relict populations of species which were more widely distributed
    during the end of the last Glacial and Early Holocene periods. This fact
    makes it particularly interesting for us to study relict ecosystems, i.e.
    those that resemble the landscape character in the past, since the Last
    Glacial Maximum before ca. 24 000 years. In the case of mires we focus on
    the distribution, ecology and conservation issues of relict plant and
    mollusc species, their disjunct distributions and long-term in situ
    survivals. The possibility of accurate dating and information about the{" "}
    <Link to="/topic-paleo">historical development</Link> allow us to
    interconnect the site age with the occurrence of relict populations, or even
    with the number of relict species at a particular site. Except for mires we
    study other <Link to="/topic-crypto">relict ecosystems</Link> as well, such
    as dry grasslands, salt marshes, lichen-rich pine forests and treeless
    alpine grasslands. In a particular detail, we focus on the relict semi-dry
    grasslands in the White Carpathians and elsewhere in the peripheral zone of
    the Carpathians. These belong to the most species-rich grasslands across the
    globe, hosting a number of relict species. We try to examine to what extent
    their uniqueness is linked with their past Quaternary history, historical as
    well as current management, and current local conditions. We try to find out
    which species of our flora might represent relicts, to recognize their
    distribution and ecological preferences, and to understand how we can
    effectively protect them for generations to come. We are also interested in
    ecosystems that were common during the different time periods since the Last
    Glacial Maximum up to the Holocene Climate Optimum, yet are rare or absent
    in the current landscape. We found a variety of such ecosystems in the
    mountain ranges of southern Siberia. Studies of these ecosystems can help us
    create a more precise picture of the glacial landscape in central Europe. In
    the same way, it was also useful to study current communities of southern
    Ural that represent apparent analogues to our forest ecosystems at the onset
    of the Holocene. At present we explore the distribution of selected species
    in the boreal and temperate zones of Northern Hemisphere, trying to
    understand the impact of Pleistocene glacial cycles on their distribution
    and evolution using molecular techniques. For this purpose we will also use
    our recent data from Alaska, which also represents a relict landscape, as
    the majority of its extent has never been glaciated during the Quaternary.
  </span>
);

export const relictEcosystemsTextCz = (
  <span>
    Jednou z pozoruhodných a unikátních vlastností{" "}
    <Link to="/topic-mires">vrchovišť a slatinišť</Link> je jejich schopnost
    poskytnout útočiště mnoha reliktním populacím řady druhů z konce poslední
    doby ledové a počátku současné doby poledové. Právě proto nás baví zkoumat
    reliktní ekosystémy, tedy takové, které různou mírou připomínají stav
    přírody v minulosti, od doby posledního maxima zalednění zhruba před 24
    tisíci lety. V případě rašelinišť sledujeme rozšíření, ekologii a
    ochranářské otázky týkající se reliktních druhů rostlin a měkkýšů, jejich
    disjunktního rozšíření a dlouhodobého přežívání na lokalitách. Možnost
    přesného datování stáří rašelinišť a poznání jejich{" "}
    <Link to="/topic-paleo">historického vývoje</Link> nám umožňuje dávat do
    souvislosti stáří lokalit a výskyt jednotlivých reliktních populací nebo i
    počet reliktních druhů na dané lokalitě. Vyjma rašelinišť se věnujeme i
    dalším <Link to="/topic-crypto">reliktním ekosystémům</Link>, jako jsou
    stepní trávníky, slaniska, lišejníkové bory nebo alpínské bezlesí. K
    nejdetailněji zkoumaným reliktním ekosystémům patří zejména stepní louky v
    Bílých Karpatech a jinde na obvodu Karpat. Tyto louky patří k druhově
    nejbohatším loukám na světě, a dodnes se na nich vyskytují reliktní druhy.
    Zkoumáme, nakolik se na jejich mimořádnosti podílí vliv dávné čtvrtohorní
    historie, historického i současného obhospodařování a současných podmínek
    prostředí. Snažíme se přijít na to, které druhy naší flóry jsou
    pravděpodobně relikty, jaké je jejich rozšíření a ekologické nároky, a jak
    je můžeme účinně chránit pro další generace. Zajímají nás i další
    ekosystémy, které byly v naší přírodě běžné v jednotlivých obdobích od
    posledního glaciálního maxima až po klimatické optimum Holocénu, a dnes jsou
    u nás vzácné nebo se už nevyskytují. Řadu takových ekosystémů jsme nalezli v
    pohořích jižní Sibiře. Jejich studiem můžeme výrazně upřesnit naše představy
    o podobě glaciální krajiny u nás. Obdobně přínosné bylo studium současných
    společenstev jižního Uralu, které vykazují nápadnou podobu s našimi lesními
    společenstvy v počátku Holocénu. Aktuálně sledujeme rozšíření vybraných
    druhů v boreální a temperátní zóně severní polokoule a snažíme se pochopit
    vliv pleistocenních zalednění na jejich rozšíření a evoluci pomocí
    molekulárních metod. K tomu nám poslouží i naše aktuální data z Aljašky,
    která rovněž představuje reliktní krajinu, jelikož většina její rozlohy
    nebyla nikdy během čtvrtohor zaledněna.
  </span>
);

export const results = {
  malacologyResults: [
    `Saito, T., Šlachtová, E., Nováková, M., Horsáková, V., Ye, B., Beran, L., & Horsák, M. (2023). Invasion history
    of Gyraulus chinensis (Gastropoda: Planorbidae) in Europe: a molecular and literature-based approach.
    Hydrobiologia, 1-13.`,
    `
    Nekola, J. C., Nováková, M., Horsák, M., & Adema, C. M. (2022). ELAV Intron 8: a single-copy sequence
    marker for shallow to deep phylogeny in Eupulmonata Hasprunar & Huber, 1990 and Hygrophila Férussac, 1822
    (Gastropoda: Mollusca). Organisms Diversity & Evolution, 1-9.`,
    `
    Horsák, M., Horsáková, V., Divíšek, J., & Nekola, J. C. (2022). Ecological niche divergence between extant and
    glacial land snail populations explained. Scientific Reports, 12, 806.`,
    `
    Horsáková, V., Líznarová, E., Razkin, O., Nekola, J. C., & Horsák, M. (2022). Deciphering “cryptic” nature of
    European rock-dwelling Pyramidula snails (Gastropoda: Stylommatophora). Contributions to Zoology, 91, 233-
    260.`,
    `
    Nekola, J. C., & Horsák, M. (2022). The impact of empirically unverified taxonomic concepts on ecological
    assemblage patterns across multiple spatial scales. Ecography, 2022(5), e06063.`,
    `
    Nekola, J. C., Divíšek, J., & Horsák, M. (2022). The nature of dispersal barriers and their impact on regional
    species pool richness and turnover. Global Ecology and Biogeography, 31, 1470-1500.`,
    `
    Coufal, R., Ryelandt, J., Peterka, T., Dítě, D., Hájková, P., Hájek, M., ... & Horsák, M. (2021). Land snail
    community patterns related to regional habitat conservation status of European spring fens. Science of The Total
    Environment, 783, 146910.`,
    `
    Lorencová, E., Beran, L., Nováková, M., Horsáková, V., Rowson, B., Hlaváč, J. Č., ... & Horsák, M. (2021).
    Invasion at the population level: a story of the freshwater snails Gyraulus parvus and G. laevis. Hydrobiologia,
    848, 4661-4671.`,
    `
    Němec, T., & Horsák, M. (2019). Specific damage recognised on land snail shells as a tool for studying
    predation intensity: differences related to habitat and predator types. Contributions to Zoology, 88, 277-296.`, 
    `Horsáková, V., Nekola, J. C., & Horsák, M. (2019). When is a “cryptic” species not a cryptic species: a
    consideration from the Holarctic micro-landsnail genus Euconulus (Gastropoda: Stylommatophora). Molecular
    Phylogenetics and Evolution, 132, 307-320.`,
    `
    Nekola, J. C., Chiba, S., Coles, B. F., Drost, C. A., Von Proschwitz, T., & Horsák, M. (2018). A phylogenetic
    overview of the genus Vertigo OF Müller, 1773 (Gastropoda: Pulmonata: Pupillidae: Vertigininae). Malacologia,
    62, 21-161.`,
    `Horsák, M., Juřičková, L., & Picka, J. (2013) Měkkýši České a Slovenské republiky. Molluscs of the Czech and Slovak Republics. Kabourek, Zlín, 264 pp. (in Czech and English)`,
    `
    Horsák, M. (2006). Mollusc community patterns and species response curves along a mineral richness gradient:
    a case study in fens. Journal of Biogeography, 33, 98-107.`,
  ],
  mireEcologyResults: [
    `Hájková, P., Horsáková, V., Peterka, T., Janeček, Š., Galvánek, D., Dítě, D., ... & Hájek, M. (2022). Conservation and restoration of Central European fens by mowing: A consensus from 20 years of experimental work. Science of The Total Environment, 846, 157293.`,
    `Hájek, M., Těšitel, J., Tahvanainen, T., Peterka, T., Jiménez-Alfaro, B., Jansen, F., Pérez-Haase, A., 
	Garbolino, E., Carbognani, M., Kolari, T.H.M., Hájková, P., Jandt, U., Aunina, L., Pawlikowski, P., 
	Ivchenko, T., Tomaselli, M., Tichý, L., Dítě, D., Plesková, Z., & Mikulášková, E. (2022). Rising temperature 
	modulates pH niches of fen species. Global Change Biology, 28, 1023-1037.`,
    `Nekola, J.C., & Horsák, M. (2022). The impact of empirically unverified taxonomic concepts on 
	ecological assemblage patterns across multiple spatial scales. Ecography, 2022, e06063.`,
    `Hájek, M., Jiménez-Alfaro, B., Hájek, O., Brancaleoni, L., Cantonati, M., Carbognani, M., Dedić, A., 
	Dítě, D., Gerdol, R., Hájková, P., Horsáková, V., Jansen, F., Kamberović, J., Kapfer, J., Kolari, T.H.M., 
	Lamentowicz, M., Lazarević, P., Mašić, E., Moeslund, J.E., Pérez-Haase, A., Peterka, T., 
	Petraglia, A., Pladevall-Izard, E., Plesková, Z., Segadelli, S., Semeniuk, Y., Singh, P.,
	 Šímová, A., Šmerdová, E., Tahvanainen, T., Tomaselli, M., Vystavna, Y., Biţă-Nicolae, C., & Horsák, M. 
	(2021). A European map of groundwater pH and calcium. Earth System Science Data, 13, 1089-1105. `,
    `Hájek, M., Dítě, D., Horsáková, V., Mikulášková, E., Peterka, T., Navrátilová, J., Jiménez-Alfaro, B., 
	Hájková, P., Tichý, L., & Horsák, M. (2020). Towards the pan-European bioindication system: 
	Assessing and testing updated hydrological indicator values for vascular plants and bryophytes in mires. 
	Ecological Indicators, 116, 106527.`,
    `Hájek, M., Horsáková, V., Hájková, P., Coufal, R., Dítě, D., Němec, T., & Horsák, M. (2020). Habitat extremity 
	and conservation management stabilise endangered calcareous fens in a changing world. 
	Science of the Total Environment, 719, 134693.`,
    `Horsáková, V., Nekola, J.C., & Horsák, M. (2019). When is a "cryptic" species not a cryptic 
	species: A consideration from the Holarctic micro-landsnail genus Euconulus (Gastropoda: Stylommatophora). 
	Molecular Phylogenetics and Evolution, 132, 307-320.`,
    `Horsáková, V., Hájek, M., Hájková, P., Dítě, D., & Horsák, M. (2018). Principal factors controlling the
        species richness of European fens differ between habitat specialists and matrix‐derived species. Diversity
        and Distributions, 24, 742-754.`,
    `Horsák, M., Polášková, V., Zhai, M., Bojková, J., Syrovátka, V., Šorfová, V., ... & Hájek, M. (2018). Spring-
        fen habitat islands in a warming climate: Partitioning the effects of mesoclimate air and water
        temperature on aquatic and terrestrial biota. Science of the Total Environment, 634, 355-365.`,
    `Navrátilová, J., Hájek, M., Navrátil, J., Hájková, P., & Frazier, R. J. (2017). Convergence and
        impoverishment of fen communities in a eutrophicated agricultural landscape of the Czech
        Republic. Applied Vegetation Science, 20, 225-235.`,
    `Peterka, T., Hájek, M., Jiroušek, M., Jiménez‐Alfaro, B., Aunina, L., Bergamini, A., ... (2017). Formalized
        classification of European fen vegetation at the alliance level. Applied Vegetation Science, 20, 124-142.`,
    `Plesková, Z., Jiroušek, M., Peterka, T., Hájek, T., Dítě, D., Hájková, P., ... & Hájek, M. (2016). Testing
        inter‐regional variation in pH niches of fen mosses. Journal of vegetation science, 27, 352-364.`,
    `Peterka, T., Pleskova, Z., Jirousek, M., & Hajek, M. (2014). Testing floristic and environmental
        differentiation of rich fens on the Bohemian Massif. Preslia, 86, 337-366.`,
    `Jiroušek, M., Poulíčková, A., Kintrová, K., Opravilová, V., Hájková, P., Rybníček, K., ... & Králová, Š. (2013).
        Long‐term and contemporary environmental conditions as determinants of the species composition of
        bog organisms. Freshwater Biology, 58, 2196-2207.`,
    `Horsák, M., Hájek, M., Spitale, D., Hájková, P., Dítě, D., & Nekola, J. C. (2012). The age of island‐like
        habitats impacts habitat specialist species richness. Ecology, 93, 1106-1114.`,
    `Hájek, M., Roleček, J., Cottenie, K., Kintrová, K., Horsák, M., Poulíčková, A., ... & Dítě, D. (2011).
        Environmental and spatial controls of biotic assemblages in a discrete semi‐terrestrial habitat:
        comparison of organisms with different dispersal abilities sampled in the same plots. Journal of
        Biogeography, 38, 1683-1693.`,
    `Jiroušek, M., Hájek, M., & Bragazza, L. (2011). Nutrient stoichiometry in Sphagnum along a nitrogen
        deposition gradient in highly polluted region of Central-East Europe. Environmental Pollution, 159, 585-
        590.`,
    `Hájek, M., Horsák, M., Hájková, P., & Dítě, D. (2006). Habitat diversity of central European fens in
        relation to environmental gradients and an effort to standardise fen terminology in ecological
        studies. Perspectives in Plant Ecology, Evolution and Systematics, 8, 97-114.`,
  ],
  palaeoEcologyResults: [
    `Hájková, P., Petr, L., Horsák, M., Jamrichová, E., & Roleček, J. (2022). Holocene history of the landscape at the biogeographical and cultural crossroads between Central and Eastern Europe (Western Podillia, Ukraine). Quaternary Science Reviews, 288, 107610.`,
    `Divíšek, J., Hájek, M., Jamrichová, E., Petr, L., Večeřa, M., Tichý, L., Willner, W., & Horsák, M. (2020). 
	Holocene matters: Landscape history accounts for current species richness of vascular plants in forests 
	and grasslands of eastern Central Europe. Journal of Biogeography, 47, 721-735.`,
    `Hájková, P., Jamrichová, E., Šolcová, A., Frodlová, J., Petr, L., Dítě, D., Hájek, M., & Horsák, M. (2020). 
	Can relict-rich communities be of an anthropogenic origin? Palaeoecological insight into conservation strategy 
	for endangered Carpathian travertine fens. Quaternary Science Reviews, 234, 106241.`,
    `Horsák, M., Limondin-Lozouet, N., Granai, S., Dabkowski, J., Divíšek, J., & Hájková, P. (2020). 
	Colonisation dynamic and diversity patterns of holocene forest snail fauna across temperate Europe: 
	The imprint of palaeoclimate changes. Quaternary Science Reviews, 240, 106367.`,
    `Šímová, A., Pánek, T., Gałka, M., Zernitskaya, V., Hájková, P., Brodská, H., Jamrichová, E., & Hájek, M. (2019). 
	Landslides increased Holocene habitat diversity on a flysch bedrock in the Western Carpathians. 
	Quaternary Science Reviews, 219, 68-83.`,
    `Frodlová, J., Hájková, P. & Horsák, M. (2018). Effect of sample size and resolution on palaeomalacological
    interpretation: a case study from Holocene calcareous-fen deposits. Journal of Quaternary Science, 33,
    68-78.`,
    `Dudová, L., Hájek, M., Petr, L., & Jankovská, V. (2018). Holocene vegetation history of the Jeseníky Mts:
    Deepening elevational contrast in pollen assemblages since late prehistory. Journal of Vegetation
    Science, 29, 371-381.`,
    `Jamrichová, E., Petr, L., Jiménez‐Alfaro, B., Jankovská, V., Dudová, L., Pokorný, P., ... & Syrovátka, V.
    (2017). Pollen‐inferred millennial changes in landscape patterns at a major biogeographical interface
    within Europe. Journal of Biogeography, 44, 2386-2397.`,
    `Hájková, P., Pařil P., Petr, L., Chattová, B., Grygar, T.M. & Heiri, O. (2016). A first chironomid-based
    summer temperature reconstruction (13-5 ka BP) around 49°N in inland Europe compared with local lake
    development. Quaternary Science Reviews, 141, 94-111.`,
    `Hájek, M., Dudová, L., Hájková, P., Roleček, J., Moutelíková, J., Jamrichová, E. & Horsák, M. (2016):
    Contrasting Holocene environmental histories may explain patterns of species richness and rarity in a
    Central European landscape. Quaternary Science Reviews, 133, 48-61.`,
    `Hájková, P., Horsák, M., Hájek, M., Jankovská, V., Jamrichová, E. & Moutelíková, J. (2015). Using multi-
    proxy palaeoecology to test a relict status of refugial populations of calcareous-fen species in the
    Western Carpathians. Holocene, 25, 702-715.`,
    `Juřičková, L., Horsák, M., Horáčková, J., Abraham, V. & Ložek, V. (2014). Patterns of land-snail succession
    in Central Europe over the last 15,000 years: main changes along environmental, spatial and temporal
    gradients. Quaternary Science Reviews, 93, 155-166.`,
    `Dudová, L., Hájková, P., Buchtová, H., & Opravilová, V. (2013). Formation, succession and landscape
    history of Central-European summit raised bogs: A multiproxy study from the Hrubý Jeseník
    Mountains. The Holocene, 23, 230-242.`,
  ],
  relictEcosystemsResults: [
    `Horsák, M., Horsáková, V., Divíšek, J. & Nekola, J.C. (2022). Ecological niche divergence between 
	extant and glacial land snail populations explained. Scientific Reports, 12, 806.`,
    `Peterka, T., Tichý, L., Horsáková, V., Hájková, P., Coufal, R., Petr, L., Dítě, D, Hradílek, Z., Hrivnák, R., 
	Jiroušek, M., Plášek, V., Plesková, Z., Singh, P., Šmerdová, E., Štechová, T., Mikulášková, E., Horsák, M., & Hájek, M. (2022). 
	The long history of rich fens supports persistence of plant and snail habitat specialists. Biodiversity and Conservation, 31, 39-57.`,
    `Hájek, M., Hájková, P. & Roleček, J. (2020). A novel dataset of permanent plots in extremely species-rich temperate grasslands. 
	Folia Geobotanica, 55, 257-268.`,
    `Dítě, D., Hájek, M., Svitková, I., Košuthová, A., Šoltés, R., & Kliment, J. (2018). Glacial-relict symptoms in
    the Western Carpathian flora. Folia Geobotanica, 53, 277-300.`,
    `Horsák, M., Hájek, M., Horsáková, V., Hlaváč, J., Hájková, P., Dítě, D., Peterka, T., Divíšek, J., Potůčková, A.
    & Preece, R.C. (2017). Refugial occurrence and ecology of the land snail Vertigo lilljeborgi in fen habitats
    in temperate mainland Europe. Journal of Molluscan Studies, 83, 451-460.`,
    `Chytrý, M., Horsák, M., Syrovátka, V., Danihelka, J., Ermakov, N., German, D.A., Hájek, M., Hájek, O.,
    Hájková, P., Horsáková, V., Kočí, M., Kubešová, S., Lustyk, P., Nekola, J.C., Preislerová, Z., Resl, P. &
    Valachovič, M. (2017). Refugial ecosystems in central Asia as indicators of biodiversity change during the
    Pleistocene-Holocene transition. Ecological Indicators, 77, 357-367.`,
    `Hájek, M., Dresler, P., Hájková, P., Hettenbergerová, E., Milo, P., Plesková, Z., & Pavonič, M. (2017). Long-
    lasting imprint of former glassworks on vegetation pattern in an extremely species-rich grassland: a
    battle of species pools on mesic soils. Ecosystems, 20, 1233-1249.`,
    `Horsák, M., Chytrý, M., Hájková, P., Hájek, M., Danihelka, J., Horsáková, V., Ermakov, N., German, D.A.,
    Kočí, M., Lustyk, P., Nekola, J.C., Preislerová, Z. & Valachovič, M. (2015). European glacial relict snails and
    plants: environmental context of their modern refugial occurrence in southern Siberia. Boreas, 44, 638-
    657.`,
    `Horsák, M., Hájek, M., Spitale, D., Hájková, P., Dítě, D. & Nekola, J.C. (2012). The age of island-like
    habitats impacts habitat specialist species richness. Ecology, 93, 1106-1114.`,
    `Hájek, M., Horsák, M., Tichý, L., Hájková, P., Dítě, D., & Jamrichová, E. (2011). Testing a relict
    distributional pattern of fen plant and terrestrial snail species at the Holocene scale: a null model
    approach. Journal of Biogeography, 38, 742-755.`,
    `Horsák, M., Chytrý, M., Pokryszko, B.M., Danihelka, J., Ermakov, N., Hájek, M., Hájková, P., Kintrová, K.,
    Kočí, M., Kubešová, S., Lustyk, P., Otýpková, Z., Pelánková, B. & Valachovič, M. (2010). Habitats of relict
    terrestrial snails in southern Siberia: lessons for the reconstruction of palaeoenvironments of full-glacial
    Europe. Journal of Biogeography, 37, 1450-1462.`,
  ],
  cryptogamologyResults: [
    `Chattová, B., Cahova, T., Pinseel, E., Kopalová, K., Kohler, T. J., Hrbáček, F., ... & Nývlt, D. (2022). Diversity, ecology, and community structure of the terrestrial diatom flora from Ulu Peninsula (James Ross Island, NE Antarctic Peninsula). Polar Biology, 45(5), 873-894.`,
    `Cahová, T., & Chattová, B. (2021). Diversity and species composition of diatom communities of Ardley Island, South Shetland Islands. Czech Polar Reports, 11(1), 25-40.`,
    `Chattová, B., Lebouvier, M., & Van de Vijver, B. (2018). Morphological and taxonomical analysis of the
    terrestrial diatom genus Humidophila (Bacillariophyta) on Ile Amsterdam and Ile Saint-Paul (Southern
    Indian Ocean). Phytotaxa, 336, 28-42.`,
    `Chattová, B., Lebouvier, M., De Haan, M., & Van de Vijver, B. (2017). The genus Luticola (Bacillariophyta)
    on Ile Amsterdam and Ile Saint-Paul (Southern Indian Ocean) with the description of two new species.
    European Journal of Taxonomy, 387, 1-17.`,
    `Mikulášková, E., Veleba, A., Šmerda, J., Knoll, A., & Hajek, M. (2017). Microsatellite variation in three
    calcium-tolerant species of peat moss detected specific genotypes of Sphagnum warnstorfii on
    magnesium-rich bedrock. Preslia, 89, 101-114.`,
    `Košuthová, A., Fernández-Brime, S., Westberg, M., & Wedin, M. (2016). Collolechia revisited and a re-
    assessment of ascus characteristics in Placynthiaceae (Peltigerales, Ascomycota). The Lichenologist, 48,
    3-12.`,
    `Mikulášková, E., Hájek, M., Veleba, A., Johnson, M. G., Hájek, T., & Shaw, J. A. (2015). Local adaptations
    in bryophytes revisited: the genetic structure of the calcium‐tolerant peatmoss Sphagnum warnstorfii
    along geographic and pH gradients. Ecology and evolution, 5, 229-242.`,
    `Košuthová, A., Svitková, I., Pišút, I., Senko, D., Valachovič, M., Zaniewski, P. T., & Hájek, M. (2015).
    Climatic gradients within temperate Europe and small-scale species composition of lichen-rich dry
    acidophilous Scots pine forests. Fungal Ecology, 14, 8-23.`,
    `Chattová, B., Lebouvier, M., & Van de Vijver, B. (2014). Freshwater diatom communities from Ile
    Amsterdam (TAAF, southern Indian Ocean). Fottea, 14, 101-119.`,
    `Košuthová, A. D., & Šibík, J. (2013). Ecological indicator values and life history traits of terricolous lichens
    of the Western Carpathians. Ecological indicators, 34, 246-259.`,
    `Mikulášková, E., Fajmonova, Z., & Hajek, M. (2012). Invasion of central-European habitats by the moss
    Campylopus introflexus. Preslia, 84, 863-886.`,
  ],
};
